import { z } from "zod";
import { slugSchema } from "./slug";
import { currencySchema, priceCentsSchema } from "./money";

export const productStatusSchema = z.enum(["active", "archived"]);
export type ProductStatus = z.infer<typeof productStatusSchema>;

/** Stock-keeping unit: human-entered, unique per catalog. */
export const skuSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._-]*$/,
    "SKU may contain letters, digits, dot, underscore and hyphen",
  );

/** A non-negative whole count of units. */
export const quantitySchema = z.number().int().nonnegative();

/** A persisted product as returned by the API. */
export const productSchema = z
  .object({
    id: z.string(),
    sku: z.string(),
    slug: slugSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(5000),
    priceCents: priceCentsSchema,
    currency: currencySchema,
    quantity: quantitySchema,
    lowStockThreshold: quantitySchema,
    status: productStatusSchema,
    /** Website ordering; older records remain purchasable by default. */
    cartEnabled: z.boolean().default(true),
    imageId: z.string().nullable(),
    /** Admin-authored rich product-page body (trusted HTML). */
    body: z.string(),
    /** Additional media ids shown as a gallery on the product page. */
    galleryImageIds: z.array(z.string()),
    /** Cached Square item-variation id this product is linked to (by SKU), if any. */
    squareVariationId: z.string().nullable(),
    /** When our count was last pushed to Square, if ever. */
    squareSyncedAt: z.string().nullable(),
    /** Cached Stripe product id this product is linked to (by SKU), if any. */
    stripeProductId: z.string().nullable(),
    /** When this product's Stripe active flag was last reconciled, if ever. */
    stripeSyncedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
  })
  .strict();
export type Product = z.infer<typeof productSchema>;

/** Catalog fields the admin edits (shared by create & update). */
const productFields = {
  sku: skuSchema,
  slug: slugSchema.optional(),
  name: z.string().min(1).max(200),
  description: z.string().max(5000).default(""),
  priceCents: priceCentsSchema,
  /** Omitted updates preserve the current value for older CMS clients. */
  cartEnabled: z.boolean().optional(),
  lowStockThreshold: quantitySchema.default(0),
  imageId: z.string().nullable().optional(),
  /** Rich product-page body (trusted HTML authored in the admin). */
  body: z.string().max(20000).default(""),
  /** Additional media ids shown as a gallery on the product page. */
  galleryImageIds: z.array(z.string()).max(50).default([]),
};

export const createProductInputSchema = z
  .object({
    ...productFields,
    /** Opening stock balance; recorded as an "initial" movement at creation. */
    initialQuantity: quantitySchema.default(0),
  })
  .strict();
export type CreateProductInput = z.infer<typeof createProductInputSchema>;

/** Update excludes quantity (changed only via stock adjustments) and status. */
export const updateProductInputSchema = z.object(productFields).strict();
export type UpdateProductInput = z.infer<typeof updateProductInputSchema>;
